import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Tooltip } from "react-tooltip";
import DataTable, { TableColumn } from "react-data-table-component";
import { useSelector } from 'react-redux';
import { RootState } from "../../../store/store";
import { useGetLoanTasks, useClaimLeadTask, getLoanTasks } from "../../../services/loanService";
import { useUnclaimTask } from "../../../services/bpmService";
import { BpmTask, BpmTaskWorkItemType, GetLoanTasksStage } from "../../../services/schemas/loanServiceSchemas";
import { toast } from "react-toastify";
import { Card, Badge, Button, Form, Row, Col } from 'react-bootstrap';
import './WorkItems.css';

// Add custom styles similar to Documents.tsx
const customStyles = {
    rows: {
        style: {
            minHeight: '45px',
        }
    },
    headCells: {
        style: {
            fontWeight: 600,
            paddingLeft: '15px',
            paddingRight: '8px',
            fontSize: '14px',
            justifyContent: 'flex-start'
        }
    },
    cells: {
        style: {
            paddingLeft: '15px',
            paddingRight: '8px',
        }
    }
};

interface WorkItemsProps {}

const WorkItems: React.FC<WorkItemsProps> = () => {
    const [clientFullScreen, setClientFullScreen] = useState(false);
    const handleFullScreen = () => setClientFullScreen(!clientFullScreen);
    const navigate = useNavigate();

    const authState = useSelector((state: RootState) => state.auth);
    const userEmail = useSelector((state: RootState) => state.auth.user?.email);
    const userGroups = useSelector((state: RootState) => state.auth.user?.userGroups) || [];
    const isAdmin = userGroups.some(role => role === 'ADMIN');

    const [activeTab, setActiveTab] = useState<'group' | 'my'>('group');
    const [groupTasks, setGroupTasks] = useState<BpmTask[]>([]);
    const [isLoadingGroupTasks, setIsLoadingGroupTasks] = useState(false);
    const [groupTasksError, setGroupTasksError] = useState<Error | null>(null);

    // Search state for group tasks
    const [groupSearchCustomerName, setGroupSearchCustomerName] = useState('');
    const [groupSearchCustomerNameInput, setGroupSearchCustomerNameInput] = useState('');
    const [groupSearchStage, setGroupSearchStage] = useState<GetLoanTasksStage | ''>('');

    // Search state for my tasks
    const [mySearchCustomerName, setMySearchCustomerName] = useState('');
    const [mySearchCustomerNameInput, setMySearchCustomerNameInput] = useState('');
    const [mySearchStage, setMySearchStage] = useState<GetLoanTasksStage | ''>('');

    // Opened from the header's work-waiting alert: /work-items?stage=CREDIT_ASSESSMENT or ?tab=my
    const location = useLocation();
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const stage = params.get('stage') as GetLoanTasksStage | null;
        const tab = params.get('tab');
        if (tab === 'my') {
            setActiveTab('my');
            setMySearchStage(stage && Object.values(GetLoanTasksStage).includes(stage) ? stage : '');
        } else if (stage && Object.values(GetLoanTasksStage).includes(stage)) {
            setActiveTab('group');
            setGroupSearchStage(stage);
        }
    }, [location.search]);

    // Function to fetch group tasks
    const fetchGroupTasks = async () => {
        if (!userGroups.length) return;

        setIsLoadingGroupTasks(true);
        setGroupTasksError(null);

        try {
            if (isAdmin) {
                // For admin users, fetch tasks for ADMIN group
                const response = await getLoanTasks({
                    assigneeGroup: 'ADMIN',
                    customerName: groupSearchCustomerName || undefined,
                    stage: groupSearchStage || undefined,
                    page: 0,
                    size: 100
                });
                setGroupTasks(response.content?.filter(task => !task.assignee) || []);
            } else {
                // For non-admin users, fetch tasks for all groups in parallel
                const responses = await Promise.all(
                    userGroups.map(group =>
                        getLoanTasks({
                            assigneeGroup: group,
                            customerName: groupSearchCustomerName || undefined,
                            stage: groupSearchStage || undefined,
                            page: 0,
                            size: 100
                        })
                    )
                );

                // Combine and deduplicate tasks
                const allGroupTasks = responses.flatMap(response => response.content || []);
                const uniqueTasks = Array.from(
                    new Map(allGroupTasks.map(task => [task.taskId, task])).values()
                );
                setGroupTasks(uniqueTasks.filter(task => !task.assignee));
            }
        } catch (error) {
            setGroupTasksError(error instanceof Error ? error : new Error('Failed to fetch group tasks'));
        } finally {
            setIsLoadingGroupTasks(false);
        }
    };

    // Debounce effect for group search customer name
    useEffect(() => {
        const timer = setTimeout(() => {
            // Only search if input is empty or has at least 3 characters
            if (groupSearchCustomerNameInput === '' || groupSearchCustomerNameInput.length >= 3) {
                setGroupSearchCustomerName(groupSearchCustomerNameInput);
            }
        }, 500); // 500ms delay

        return () => clearTimeout(timer);
    }, [groupSearchCustomerNameInput]);

    // Debounce effect for my tasks search customer name
    useEffect(() => {
        const timer = setTimeout(() => {
            // Only search if input is empty or has at least 3 characters
            if (mySearchCustomerNameInput === '' || mySearchCustomerNameInput.length >= 3) {
                setMySearchCustomerName(mySearchCustomerNameInput);
            }
        }, 500); // 500ms delay

        return () => clearTimeout(timer);
    }, [mySearchCustomerNameInput]);

    // Fetch group tasks using direct API calls
    useEffect(() => {
        fetchGroupTasks();
    }, [userGroups, isAdmin, groupSearchCustomerName, groupSearchStage]);

    // Get personal tasks (assigned to the current user)
    const {
        data: myTasksData,
        isLoading: isLoadingMyTasks,
        error: myTasksError,
        refetch: refetchMyTasks
    } = useGetLoanTasks(
        {
            assignee: userEmail || '',
            customerName: mySearchCustomerName || undefined,
            stage: mySearchStage || undefined,
            page: 0,
            size: 100
        },
        {
            query: {
                enabled: Boolean(userEmail)
            }
        }
    );

    // Mutation for claiming tasks
    const claimTaskMutation = useClaimLeadTask({
        mutation: {
            onSuccess: () => {
                toast.success("Task claimed successfully");
                // Refresh both task lists
                refetchMyTasks();
                // Re-fetch group tasks
                fetchGroupTasks();
            },
            onError: (error) => {
                toast.error(`Failed to claim task: ${error instanceof Error ? error.message : 'Unknown error'}`);
            }
        }
    });

    // Mutation for unclaiming tasks
    const unclaimTaskMutation = useUnclaimTask({
        mutation: {
            onSuccess: () => {
                toast.success("Task unclaimed successfully");
                // Refresh both task lists
                refetchMyTasks();
                // Re-fetch group tasks
                fetchGroupTasks();
            },
            onError: (error) => {
                toast.error(`Failed to unclaim task: ${error instanceof Error ? error.message : 'Unknown error'}`);
            }
        }
    });

    const myTasks = myTasksData?.content || [];

    // Loading and error states based on active tab
    const isLoading = activeTab === 'group' ? isLoadingGroupTasks : isLoadingMyTasks;
    const error = activeTab === 'group' ? groupTasksError : (myTasksError ? new Error(myTasksError.message) : null);
    const tasks = activeTab === 'group' ? groupTasks : myTasks;

    const handleViewWorkItem = (taskId: string) => {
        navigate(`/work-items/${taskId}`);
    };

    const handleProcessWorkItem = (taskId: string, workItemType: BpmTaskWorkItemType) => {
        if (workItemType === BpmTaskWorkItemType.PROCESS_LEAD) {
            // For PROCESS_LEAD work item type, navigate to the loan application wizard with the taskId
            navigate(`/loan-application/new?taskId=${encodeURIComponent(taskId)}`);
        } else if (workItemType === BpmTaskWorkItemType.VERIFY_DOCUMENTS) {
            // For VERIFY_DOCUMENTS work item type, navigate to the Verification component
            navigate(`/verification/${encodeURIComponent(taskId)}`);
        } else if (workItemType === BpmTaskWorkItemType.UPLOAD_DOCUMENTS) {
            // For UPLOAD_DOCUMENTS work item type, navigate to the LoanApplicationDocuments component
            navigate(`/loan-application-documents/${encodeURIComponent(taskId)}?fromTask=true`);
        } else if (workItemType === BpmTaskWorkItemType.CREDIT_ASSESSMENT) {
            // For CREDIT_ASSESSMENT work item type, navigate to the CreditAssessmentDetails component
            navigate(`/credit-assessment/${encodeURIComponent(taskId)}`);
        } else if (workItemType === BpmTaskWorkItemType.UPLOAD_AGREEMENT) {
            // For GENERATE_AGREEMENT work item type, navigate to the LoanAgreement component
            navigate(`/loan-agreement/${encodeURIComponent(taskId)}`);
        } else if (workItemType === BpmTaskWorkItemType.VERIFY_COLLATERAL) {
            // For VERIFY_COLLATERAL work item type, navigate to the CollateralVerificationDetails component
            navigate(`/collateral-verification/${encodeURIComponent(taskId)}`);
        } else if (workItemType === BpmTaskWorkItemType.APPROVE_LOAN) {
            // The Credit Committee's decision on what credit recommended
            navigate(`/credit-approval/${encodeURIComponent(taskId)}`);
        } else if (workItemType === BpmTaskWorkItemType.DISBURSE_FUNDS) {
            // For DISBURSE_FUNDS work item type, navigate to the DisbursementDetails component
            navigate(`/disbursements/${encodeURIComponent(taskId)}`);
        } else {
            // For other work item types, use the existing behavior
            navigate(`/work-items/${taskId}/process`);
        }
    };

    const handleClaimWorkItem = (taskId: string) => {
        if (!userEmail) {
            toast.error("User email not found. Please log in again.");
            return;
        }

        claimTaskMutation.mutate({
            taskId,
            params: {
                userId: userEmail
            }
        });
    };

    const handleUnclaimWorkItem = (taskId: string) => {
        unclaimTaskMutation.mutate({
            taskId
        });
    };

    // Format the work item type for display
    const formatWorkItemType = (type: BpmTaskWorkItemType) => {
        return type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
    };

    // Get custom badge style for better color distinction
    const getWorkItemTypeBadgeStyle = (type: BpmTaskWorkItemType): React.CSSProperties => {
        switch (type) {
            case BpmTaskWorkItemType.PROCESS_LEAD:
                return { backgroundColor: '#212529', color: 'white' }; // Dark
            case BpmTaskWorkItemType.CAPTURE_APPLICATION:
                return { backgroundColor: '#0d6efd', color: 'white' }; // Blue
            case BpmTaskWorkItemType.UPLOAD_DOCUMENTS:
                return { backgroundColor: '#0dcaf0', color: 'black' }; // Cyan
            case BpmTaskWorkItemType.VERIFY_DOCUMENTS:
                return { backgroundColor: '#ffc107', color: 'black' }; // Yellow
            case BpmTaskWorkItemType.CREDIT_ASSESSMENT:
                return { backgroundColor: '#dc3545', color: 'white' }; // Red
            case BpmTaskWorkItemType.VERIFY_COLLATERAL:
                return { backgroundColor: '#6f42c1', color: 'white' }; // Purple
            case BpmTaskWorkItemType.REVIEW_APPLICATION:
                return { backgroundColor: '#20c997', color: 'white' }; // Teal
            case BpmTaskWorkItemType.APPROVE_LOAN:
                return { backgroundColor: '#198754', color: 'white' }; // Green
            case BpmTaskWorkItemType.UPLOAD_AGREEMENT:
                return { backgroundColor: '#fd7e14', color: 'white' }; // Orange
            case BpmTaskWorkItemType.DISBURSE_FUNDS:
                return { backgroundColor: '#28a745', color: 'white' }; // Success Green
            default:
                return { backgroundColor: '#6c757d', color: 'white' }; // Gray
        }
    };

    // Add this function to get icons for work item types
    const getWorkItemTypeIcon = (type: BpmTaskWorkItemType) => {
        switch (type) {
            case BpmTaskWorkItemType.VERIFY_DOCUMENTS:
                return 'bi-file-check';
            case BpmTaskWorkItemType.UPLOAD_DOCUMENTS:
                return 'bi-cloud-upload';
            case BpmTaskWorkItemType.CREDIT_ASSESSMENT:
                return 'bi-cash-stack';
            case BpmTaskWorkItemType.CAPTURE_APPLICATION:
                return 'bi-file-earmark-text';
            case BpmTaskWorkItemType.UPLOAD_AGREEMENT:
                return 'bi-file-earmark-contract';
            case BpmTaskWorkItemType.REVIEW_APPLICATION:
                return 'bi-clipboard-check';
            case BpmTaskWorkItemType.APPROVE_LOAN:
                return 'bi-check-circle';
            case BpmTaskWorkItemType.PROCESS_LEAD:
                return 'bi-person-plus';
            case BpmTaskWorkItemType.VERIFY_COLLATERAL:
                return 'bi-shield-check';
            case BpmTaskWorkItemType.DISBURSE_FUNDS:
                return 'bi-cash';
            default:
                return 'bi-tasks';
        }
    };

    const getTaskCustomerName = (task: BpmTask): string => {
        return task.company?.companyName || task.fullName || '';
    };

    // Columns for group tasks (unassigned)
    const groupTasksColumns: TableColumn<BpmTask>[] = [
        {
            name: "Customer",
            cell: (row) => (
                <div className="d-flex flex-column">
                    <div className="fw-bold text-primary">{getTaskCustomerName(row)}</div>
                </div>
            ),
            sortable: true,
            sortFunction: (a, b) => getTaskCustomerName(a).localeCompare(getTaskCustomerName(b)),
            width: '30%',
            style: { paddingLeft: '15px' }
        },
        {
            name: "Stage",
            cell: (row) => (
                <div className="d-flex align-items-center">
                    <span
                        className="badge"
                        style={getWorkItemTypeBadgeStyle(row.workItemType)}
                    >
                        <i className={`bi ${getWorkItemTypeIcon(row.workItemType)} me-1`}></i>
                        {formatWorkItemType(row.workItemType)}
                    </span>
                </div>
            ),
            sortable: true,
            sortFunction: (a, b) => a.workItemType.localeCompare(b.workItemType),
            width: '25%'
        },
        {
            name: "Created",
            cell: (row) => (
                <div className="d-flex flex-column">
                    <div className="fw-medium">{new Date(row.createdDate).toLocaleDateString()}</div>
                </div>
            ),
            sortable: true,
            sortFunction: (a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime(),
            width: '20%'
        },
        {
            name: 'Actions',
            cell: (row: BpmTask) => (
                <div className="d-flex justify-content-center w-100">
                    <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => handleClaimWorkItem(row.taskId)}
                        disabled={claimTaskMutation.isPending}
                        title="Claim this work item"
                        className="p-1"
                        style={{ fontSize: '12px' }}
                    >
                        <i className={`bi ${claimTaskMutation.isPending ? 'bi-hourglass-split' : 'bi-person-check'} me-1`}></i>
                        {claimTaskMutation.isPending ? 'Claiming...' : 'Claim'}
                    </Button>
                </div>
            ),
            width: '120px'
        },
    ];

    // Columns for my tasks (assigned to me)
    const myTasksColumns: TableColumn<BpmTask>[] = [
        {
            name: "Customer",
            cell: (row) => (
                <div className="d-flex flex-column">
                    <div className="fw-bold text-primary">{getTaskCustomerName(row)}</div>
                </div>
            ),
            sortable: true,
            sortFunction: (a, b) => getTaskCustomerName(a).localeCompare(getTaskCustomerName(b)),
            width: '25%',
            style: { paddingLeft: '15px' }
        },
        {
            name: "Stage",
            cell: (row) => (
                <div className="d-flex align-items-center">
                    <span
                        className="badge"
                        style={getWorkItemTypeBadgeStyle(row.workItemType)}
                    >
                        <i className={`bi ${getWorkItemTypeIcon(row.workItemType)} me-1`}></i>
                        {formatWorkItemType(row.workItemType)}
                    </span>
                </div>
            ),
            sortable: true,
            sortFunction: (a, b) => a.workItemType.localeCompare(b.workItemType),
            width: '15%'
        },
        {
            name: "Created",
            cell: (row) => (
                <div className="d-flex flex-column">
                    <div className="fw-medium">{new Date(row.createdDate).toLocaleDateString()}</div>
                </div>
            ),
            sortable: true,
            sortFunction: (a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime(),
            width: '15%'
        },
        {
            name: "Due",
            cell: (row) => (
                <div className="d-flex flex-column">
                    {row.dueDate && (
                        <div className="fw-medium">{new Date(row.dueDate).toLocaleDateString()}</div>
                    )}
                </div>
            ),
            sortable: true,
            sortFunction: (a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime(),
            width: '15%'
        },
        {
            name: 'Actions',
            cell: (row: BpmTask) => (
                <div className="d-flex justify-content-center gap-1 w-100">
                    <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => handleProcessWorkItem(row.taskId, row.workItemType)}
                        title="Process work item"
                        className="p-2"
                    >
                        <i className="bi bi-play-fill"></i>
                    </Button>
                    <Button
                        size="sm"
                        variant="outline-warning"
                        onClick={() => handleUnclaimWorkItem(row.taskId)}
                        disabled={unclaimTaskMutation.isPending}
                        title={unclaimTaskMutation.isPending ? "Unclaiming work item..." : "Unclaim this work item"}
                        className="p-2"
                    >
                        <i className={`bi ${unclaimTaskMutation.isPending ? 'bi-hourglass-split' : 'bi-person-dash'}`}></i>
                    </Button>
                </div>
            ),
            width: '120px'
        },
    ];

    return (
        <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3 page-fade-in">
            <div className="container-fluid">
                {/* Breadcrumb */}
                <div className="row mb-3">
                    <div className="col">
                        <div className="page-breadcrumb d-flex align-items-center">
                            <div className="pe-3">
                                <div className="px-2 rounded-circle bg-primary-light hover-scale" style={{ transition: 'all 0.3s ease' }}>
                                    <i className="bi bi-briefcase" style={{ color: '#00FF00' }}></i>
                                </div>
                            </div>
                            <div className="flex-fill">
                                <ol className="breadcrumb mb-0">
                                    <li className="breadcrumb-item"><Link to="/">Home</Link></li>
                                    <li className="breadcrumb-item active" aria-current="page">Tasks</li>
                                </ol>
                            </div>
                            <div className="ms-auto">
                                <button
                                    className="btn btn-sm btn-outline-primary me-2 btn-enhanced"
                                    onClick={() => {
                                        refetchMyTasks();
                                        fetchGroupTasks();
                                    }}
                                >
                                    <i className="bi bi-arrow-clockwise me-1"></i> Refresh
                                </button>
                                <button
                                    className="btn btn-sm btn-outline-secondary btn-enhanced"
                                    onClick={handleFullScreen}
                                >
                                    {clientFullScreen ? (
                                        <><i className="bi bi-fullscreen-exit me-1"></i> Exit Fullscreen</>
                                    ) : (
                                        <><i className="bi bi-fullscreen me-1"></i> Fullscreen</>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tab navigation */}
                <div className="row mb-3">
                    <div className="col">
                        <ul className="nav nav-tabs">
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'group' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('group')}
                                >
                                    <i className="bi bi-people-fill me-1"></i>
                                    Tasks
                                    <span className="badge btn-primary ms-2">{groupTasks.length}</span>
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'my' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('my')}
                                >
                                    <i className="bi bi-person-fill me-1"></i>
                                    My Tasks
                                    <span className="badge btn-primary ms-2">{myTasks.length}</span>
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Search filters */}
                <div className="row mb-3">
                    <div className="col-12">
                        <Card>
                            <Card.Body>
                                <Row className="g-3">
                                    <Col md={5}>
                                        <Form.Group>
                                            <Form.Label className="small fw-semibold mb-1">
                                                <i className="bi bi-person me-1"></i>
                                                Customer Name
                                                {activeTab === 'group' && groupSearchCustomerNameInput.length > 0 && groupSearchCustomerNameInput.length < 3 && (
                                                    <small className="text-muted ms-2">(min 3 characters)</small>
                                                )}
                                                {activeTab === 'my' && mySearchCustomerNameInput.length > 0 && mySearchCustomerNameInput.length < 3 && (
                                                    <small className="text-muted ms-2">(min 3 characters)</small>
                                                )}
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Search by customer name (min 3 characters)..."
                                                value={activeTab === 'group' ? groupSearchCustomerNameInput : mySearchCustomerNameInput}
                                                onChange={(e) => {
                                                    if (activeTab === 'group') {
                                                        setGroupSearchCustomerNameInput(e.target.value);
                                                    } else {
                                                        setMySearchCustomerNameInput(e.target.value);
                                                    }
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={5}>
                                        <Form.Group>
                                            <Form.Label className="small fw-semibold mb-1">
                                                <i className="bi bi-funnel me-1"></i>
                                                Stage
                                            </Form.Label>
                                            <Form.Select
                                                value={activeTab === 'group' ? groupSearchStage : mySearchStage}
                                                onChange={(e) => {
                                                    const value = e.target.value as GetLoanTasksStage | '';
                                                    if (activeTab === 'group') {
                                                        setGroupSearchStage(value);
                                                    } else {
                                                        setMySearchStage(value);
                                                    }
                                                }}
                                            >
                                                <option value="">All Stages</option>
                                                <option value={GetLoanTasksStage.PROCESS_LEAD}>Process Lead</option>
                                                <option value={GetLoanTasksStage.CAPTURE_APPLICATION}>Capture Application</option>
                                                <option value={GetLoanTasksStage.UPLOAD_DOCUMENTS}>Upload Documents</option>
                                                <option value={GetLoanTasksStage.VERIFY_DOCUMENTS}>Verify Documents</option>
                                                <option value={GetLoanTasksStage.CREDIT_ASSESSMENT}>Credit Assessment</option>
                                                <option value={GetLoanTasksStage.VERIFY_COLLATERAL}>Verify Collateral</option>
                                                <option value={GetLoanTasksStage.REVIEW_APPLICATION}>Review Application</option>
                                                <option value={GetLoanTasksStage.APPROVE_LOAN}>Approve Loan</option>
                                                <option value={GetLoanTasksStage.UPLOAD_AGREEMENT}>Upload Agreement</option>
                                                <option value={GetLoanTasksStage.DISBURSE_FUNDS}>Disburse Funds</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={2} className="d-flex align-items-end">
                                        <Button
                                            variant="outline-secondary"
                                            className="w-100"
                                            onClick={() => {
                                                if (activeTab === 'group') {
                                                    setGroupSearchCustomerName('');
                                                    setGroupSearchCustomerNameInput('');
                                                    setGroupSearchStage('');
                                                } else {
                                                    setMySearchCustomerName('');
                                                    setMySearchCustomerNameInput('');
                                                    setMySearchStage('');
                                                }
                                            }}
                                        >
                                            <i className="bi bi-x-circle me-1"></i>
                                            Clear
                                        </Button>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </div>
                </div>

                {/* Work items table */}
                <div className="row">
                    <div className="col-12">
                        <Card>
                            <Card.Header>
                                <h6 className="card-title mb-0">
                                    {activeTab === 'group' ? 'Available Team Tasks' : 'My Assigned Tasks'}
                                </h6>
                            </Card.Header>
                            <Card.Body>
                                {error ? (
                                    <div className="alert alert-danger">
                                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                        Error loading work items: {error instanceof Error ? error.message : 'Unknown error'}
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <DataTable
                                            columns={activeTab === 'group' ? groupTasksColumns : myTasksColumns}
                                            data={tasks}
                                            pagination
                                            paginationPerPage={10}
                                            paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                            highlightOnHover
                                            striped
                                            responsive
                                            progressPending={isLoading}
                                            customStyles={customStyles}
                                            persistTableHead
                                            fixedHeader
                                            progressComponent={
                                                <div className="text-center py-3">
                                                    <div className="spinner-border text-primary" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                </div>
                                            }
                                            noDataComponent={
                                                <div className="p-4 text-center">
                                                    <i className="bi bi-inbox fs-1 text-muted"></i>
                                                    <p className="mt-2">No work items found</p>
                                                </div>
                                            }
                                        />
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WorkItems;
